import React, { useState } from 'react';
import { Settings, Eye, EyeOff, RotateCcw } from 'lucide-react';

interface DashboardLayout {
  showStats: boolean;
  showQuickActions: boolean;
  showNotifications: boolean;
  showMessages: boolean;
  showProjectSelector: boolean;
}

interface DashboardCustomizerProps {
  layout: DashboardLayout;
  onLayoutChange: (layout: DashboardLayout) => void;
}

export const DashboardCustomizer: React.FC<DashboardCustomizerProps> = ({
  layout,
  onLayoutChange
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleSection = (section: keyof DashboardLayout) => {
    onLayoutChange({
      ...layout,
      [section]: !layout[section]
    });
  };

  const resetLayout = () => {
    onLayoutChange({
      showStats: true,
      showQuickActions: true,
      showNotifications: true,
      showMessages: true,
      showProjectSelector: true
    });
  };

  const sections = [
    { key: 'showStats' as const, label: 'Statistiques', icon: '📊' },
    { key: 'showQuickActions' as const, label: 'Actions rapides', icon: '⚡' },
    { key: 'showProjectSelector' as const, label: 'Sélecteur de projet', icon: '📁' },
    { key: 'showNotifications' as const, label: 'Notifications', icon: '🔔' },
    { key: 'showMessages' as const, label: 'Messages', icon: '💬' }
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 text-white/80 hover:text-white text-sm hover:scale-105"
      >
        <Settings className="w-4 h-4" />
        <span>Personnaliser</span>
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 mt-2 w-64 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-20 animate-scale-in">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Sections du tableau de bord</h3>
                <button
                  onClick={resetLayout}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded transition-colors"
                  title="Réinitialiser"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.key}
                    onClick={() => toggleSection(section.key)}
                    className="w-full flex items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 hover:scale-105"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">{section.icon}</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{section.label}</span>
                    </div>
                    {layout[section.key] ? (
                      <Eye className="w-4 h-4 text-green-600" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};